import { Map as IMap, List as IList } from 'immutable';
import { createSelector } from 'reselect';

import { newItemIsPlastic } from '../item/newItemForm';

import {
    PROMO_STORE_KEY,
    ACTIVE_PROMO,
    REJECTED_PROMO_CODE,
    PROMO_ERROR,
    PROMO_STATUS,
    PROMO_REWARDS,
    PROMO_REWARDS_STATE,
    DIGITAL_PROMO_REWARD_CARDS,
    PLASTIC_PROMO_REWARD_CARDS,
    SHIPPING_PROMO_REWARDS,
    ACCESSORY_PROMO_REWARDS,
    DISCOUNT_PROMO_REWARD,
    REWARDS_REQUEST_STATE
} from './promoModule';


export const getPromoState = state => state.get(PROMO_STORE_KEY, IMap());


export const getActivePromo = createSelector(
    getPromoState,
    promoState => promoState.get(ACTIVE_PROMO, null)
);


export const getRejectedPromo = createSelector(
    getPromoState,
    promoState => promoState.get(REJECTED_PROMO_CODE, null)
);


export const getPromoError = createSelector(
    getPromoState,
    promoState => promoState.get(PROMO_ERROR, null)
);


export const getPromoStatus = createSelector(
    getPromoState,
    promoState => promoState.get(PROMO_STATUS, IMap())
);


export const getPromoRewards = createSelector(
    getPromoState,
    promoState => promoState.get(PROMO_REWARDS, IMap())
);

export const getPromoRewardsState = createSelector(
    getPromoRewards,
    rewards => (rewards ? rewards.get(PROMO_REWARDS_STATE) : undefined)
);

export const getDigitalPromoCards = createSelector(
    getPromoRewards,
    rewards => (rewards ? rewards.get(DIGITAL_PROMO_REWARD_CARDS, IList()) : IList())
);

export const getPlasticPromoCards = createSelector(
    getPromoRewards,
    rewards => (rewards ? rewards.get(PLASTIC_PROMO_REWARD_CARDS, IList()) : IList())
);

export const getShippingPromoData = createSelector(
    getPromoRewards,
    rewards => (rewards ? rewards.get(SHIPPING_PROMO_REWARDS, IMap()) : IMap())
);

export const getAccessoriesPromoData = createSelector(
    getPromoRewards,
    rewards => (rewards ? rewards.get(ACCESSORY_PROMO_REWARDS, IMap()) : IMap())
);


export const getDiscountRewardAmount = createSelector(
    getPromoRewards,
    rewards => (rewards ? 0 - rewards.get(DISCOUNT_PROMO_REWARD, 0) : 0)
);

export const hasPlasticPromoCards = createSelector(
    getPlasticPromoCards,
    plasticList => plasticList.length > 0
);


export const getPromoRewardCards = createSelector(
    getDigitalPromoCards,
    getPlasticPromoCards,
    (digital, plastic) => digital.concat(plastic)
);



export const getPromoRewardsCount = createSelector(
    getPromoRewardCards,
    rewards => rewards.size
);


export const hasPromoRewards = createSelector(
    getPromoRewardsCount,
    count => count > 0
);


export const getPromoLabel = promo => ((promo && IMap.isMap(promo)) ? promo.get('displayLabel') : '');

export const getPromoCertType = promo => promo.get('certType');

export const getPromoDeliveryDate = promo => promo.get('deliveryDate', '');


export const getPromoTerms = createSelector(
    getActivePromo,
    promo => (promo ? promo.get('terms') : '')
);

export const hasDigitalBanners = createSelector(
    getActivePromo,
    promo => (promo ? !!promo.get('digitalBannerSmall') && !!promo.get('digitalBannerLarge') : false)
);

export const hasPlasticBanners = createSelector(
    getActivePromo,
    promo => (promo ? !!promo.get('plasticBannerSmall') && !!promo.get('plasticBannerLarge') : false)
);

export const hasSoldOutBanners = createSelector(
    getActivePromo,
    promo => (promo ? !!promo.get('soldOutBannerSmall') && !!promo.get('soldOutBannerLarge') : false)
);

export const getDigitalBanners = createSelector(
    getActivePromo,
    promo => (promo ? {
        small: promo.get('digitalBannerSmall'),
        large: promo.get('digitalBannerLarge')
    } : null)
);

export const getPlasticBanners = createSelector(
    getActivePromo,
    promo => (promo ? {
        small: promo.get('plasticBannerSmall'),
        large: promo.get('plasticBannerLarge')
    } : null)
);

export const getSoldOutBanners = createSelector(
    getActivePromo,
    promo => (promo ? {
        small: promo.get('soldOutBannerSmall'),
        large: promo.get('soldOutBannerLarge')
    } : null)
);

export const activePromoSoldOut = createSelector(
    getActivePromo,
    promo => (promo ? promo.get('soldOut') : false)
);

export const getPromoBanners = createSelector(
    [
        newItemIsPlastic,
        hasDigitalBanners,
        hasPlasticBanners,
        getDigitalBanners,
        getPlasticBanners,
        activePromoSoldOut,
        getSoldOutBanners
    ],
    (
        isPlastic,
        hasDigital,
        hasPlastic,
        digital,
        plastic,
        soldOut,
        soldOutBanners
    ) => {
        if (soldOut) {
            return soldOutBanners;
        } if (hasPlastic && (isPlastic || !hasDigital)) {
            return plastic;
        } if (hasDigital) {
            return digital;
        }
        return null;
    }
);

export const getActivePromoCode = createSelector(
    getActivePromo,
    promo => (promo ? promo.get('promoCode') : null)
);



export const getMatchingShippingPromoMethod = (promoShippingMethods, shippingMethod) => (
    promoShippingMethods !== null && shippingMethod !== null
        ? promoShippingMethods.get(shippingMethod.get('token', ''))
        : undefined
);

export const getPromoIsLoading = createSelector(
    getPromoState,
    promo => promo.get('promoLoading', false)
);


export const isStillFetchingPromoRewards = createSelector(
    getActivePromo,
    getPromoRewardsState,
    (promo, rewardState) => (promo && rewardState === REWARDS_REQUEST_STATE.ACTIVE)
);
